/**
 * 2.0 Today, Part 1 frame 1k (strings 30 to 32): the status row.
 *
 * One row, only when true. Today the one true thing is "Apple Health has not
 * synced for more than a day"; when nothing is true the row is ABSENT and
 * nothing takes its place (no gap, no placeholder).
 *
 * It takes no props and reads the SAME Apple Health state source as
 * `HealthHomeSlot`: `useHealthSyncStatus`, which already gates itself on
 * `health_home_v1` AND the native app (off either, `status` stays null and this
 * renders nothing). "Sync now" runs exactly `refresh` from that hook, the
 * handler the legacy icon button ran, so a minor without guardian health
 * consent fails the same way today's control fails: this row adds no error copy.
 *
 * The never-synced connect state is not this row's: it stays with
 * `HealthHomeSlot` (D11), which the 2.0 Today mounts with `v2` so its stale
 * chip yields to this row. W1-TODAY-WIRE mounts this component.
 *
 * W1-TODAY-FIX1 (F2, F3): on the 2.0 Today the row and the hero's connect entry read
 * ONE status, not two. `HealthSyncProvider` runs `useHealthSyncStatus` once, above the
 * page, and this row and `HealthHomeSlot v2` read it through context: one
 * `GET /api/health-metrics/status`, exactly as the legacy Today makes (TWR-10), and the
 * cold-load gate can wait for it (`loaded`) so the row never arrives after the swap.
 * With no provider (the row on its own, as the card tests mount it) it reads the hook
 * itself, as before.
 */
import { createContext, useContext, type ReactNode } from 'react';
import {
  useHealthSyncStatus,
  type HealthSyncStatus,
  type UseHealthSyncStatus,
} from '../../../hooks/useHealthSyncStatus';
import { isSyncStale, syncLongDayLabel } from '../../../lib/health/healthSyncLabel';
import styles from './StatusRow.module.css';

const SharedHealthSync = createContext<UseHealthSyncStatus | null>(null);

/**
 * The one reader of the Apple Health sync status for the 2.0 Today. Mounted only
 * when `today_2_0_v1` is on, so with the flag off nothing here exists and the legacy
 * `HealthHomeSlot` reads the hook itself, as it always did.
 */
export function HealthSyncProvider({ children }: { children: ReactNode }) {
  const sync = useHealthSyncStatus();
  return <SharedHealthSync.Provider value={sync}>{children}</SharedHealthSync.Provider>;
}

/** The shared status, or null where no `HealthSyncProvider` is above (everywhere but the 2.0 Today). */
export function useSharedHealthSync(): UseHealthSyncStatus | null {
  return useContext(SharedHealthSync);
}

/**
 * The long-day label the stale row prints, or null when the row draws nothing. The one
 * place the row's own rule lives, so `healthRowFor` (the cold-load skeleton's question)
 * and `StatusRowView` (the row) cannot disagree.
 */
function staleRowDay(status: HealthSyncStatus | null): string | null {
  const lastDay = status?.lastSyncedDay ?? null;
  // Never synced: the connect CTA is HealthHomeSlot's (D11), not this row's.
  if (!lastDay) return null;
  // Fresh (today or yesterday): nothing is true, so no row.
  if (!isSyncStale(lastDay)) return null;
  // A stamp the label cannot read says nothing true, so there is no row. The status
  // endpoint writes real days, so this is a guard, not a state (F7: it used to print
  // "Apple Health last synced recently.", a sentence nobody ratified).
  return syncLongDayLabel(lastDay) || null;
}

/**
 * W1-TODAY-FIX2 (R2): which Apple Health row the loaded 2.0 page draws for this status,
 * so the cold-load skeleton can hold that row's place once the status is known and the
 * week card does not move at the swap. `connect` is the never-synced entry
 * (`HealthHomeSlot v2`, inside the hero card), `stale` is this row, with the long-day
 * label it will print (the skeleton draws that sentence's words as bars, so a longer day
 * wraps as the real one does; the label is never rendered by the skeleton); null is
 * nothing (still loading, a fresh sync, web, or `health_home_v1` off, where the hook holds
 * no status). The same rules as the two components, one place each.
 */
export type HealthRow = { kind: 'connect' } | { kind: 'stale'; day: string };
export function healthRowFor(sync: UseHealthSyncStatus | null): HealthRow | null {
  if (!sync || !sync.loaded || !sync.status) return null;
  if (!sync.status.lastSyncedDay) return { kind: 'connect' };
  const day = staleRowDay(sync.status);
  return day ? { kind: 'stale', day } : null;
}

/**
 * Ask 3, string 16: the stale row's second sentence. The troubleshooting line that
 * used to follow it (string 22) lives on the Settings Apple Health screen.
 */
export const STALE_ROW_SECOND_SENTENCE = 'Recent runs and sleep may be missing.';

/**
 * The "Sync now" pill, drawn once for both of its homes: this row and the Settings Apple
 * Health screen (Ask 3, frame 3c). It runs exactly `refresh` from the status it is given,
 * so the two homes cannot sync by different handlers.
 */
export function SyncNowButton({ sync }: { sync: UseHealthSyncStatus }) {
  const { refreshing, refresh } = sync;
  return (
    <button
      type="button"
      className={styles.sync}
      onClick={refresh}
      disabled={refreshing}
      aria-busy={refreshing || undefined}
    >
      Sync now
    </button>
  );
}

function StatusRowView({ sync }: { sync: UseHealthSyncStatus }) {
  const { status, loaded } = sync;

  if (!loaded) return null;

  const day = staleRowDay(status);
  if (!day) return null;

  return (
    <div className={styles.row}>
      <span className={styles.dot} aria-hidden />
      <span className={styles.text}>
        <strong className={styles.lead}>{`Apple Health last synced ${day}.`}</strong>{' '}
        {STALE_ROW_SECOND_SENTENCE}
      </span>
      <SyncNowButton sync={sync} />
    </div>
  );
}

/** The row reading the hook itself (no provider above). */
function StatusRowOwn() {
  return <StatusRowView sync={useHealthSyncStatus()} />;
}

export function StatusRow() {
  const shared = useSharedHealthSync();
  return shared ? <StatusRowView sync={shared} /> : <StatusRowOwn />;
}
